import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { BaseLoadComponent } from '@shared/components/classes/base-load.component';
import { Resources } from '@app/resources';
import { Observable } from 'rxjs';
import { FullNamePipe } from '@common/pipe/full-name.pipe';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { UtilityTypeBadgeComponent } from '@shared/components/utility-type-badge/utility-type-badge.component';
import { CalendarModule } from '@primeng/calendar';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { RenterInformationComponent } from '@modules/universal-billing/apartments/apartment-profile/renter-information/renter-information.component';
import { ApartmentInvoicesComponent } from './tabs/apartment-invoices/apartment-invoices.component';
import { ApartmentPaymentsComponent } from './tabs/apartment-payments/apartment-payments.component';
import { ApartmentReadingsComponent } from './tabs/apartment-readings/apartment-readings.component';
import {
  Apartment,
  ApartmentInformation,
  RenterInformation
} from '@modules/universal-billing/apartments/apartments.interface';
import { ApartmentsService } from '@modules/universal-billing/apartments/apartments.service';
import { ApartmentContractsComponent } from './tabs/apartment-contracts/apartment-contracts.component';
import { ApartmentCardComponent } from './apartment-card/apartment-card.component';
import { ContractsComponent } from '@modules/universal-billing/property-management/contracts/contracts.component';
import { takeUntil } from "rxjs/operators";
import { BalanceComponent } from '@modules/universal-billing/renters/renters-details/balance/balance.component';
import { RentType } from '@modules/universal-billing/residents/resident.enum';
import { ResourceByIdPipe } from '@shared/pipes/resource-by-id.pipe';
import { DocumentsComponent } from '@modules/universal-billing/accounting/documents/documents.component';
import { MetersComponent } from '@modules/universal-billing/meters/meters.component';


enum TabId {
  Contracts = 'contracts',
  Meters = 'meters',
  Readings = 'readings',
  Payments = 'payments',
  Invoices = 'invoices',
  Documents='documents'
}

interface Tab {
  id: TabId;
  titleKey: string;
  iconClass: string;
}

@Component({
  selector: 'app-apartment-profile',
  standalone: true,
  imports: [
    CommonModule,
    FullNamePipe,
    PageTitleComponent,
    TranslatePipe,
    DatePipe,
    SpinnerComponent,
    UtilityTypeBadgeComponent,
    RenterInformationComponent,
    CalendarModule,
    FormsModule,
    ReactiveFormsModule,
    ApartmentInvoicesComponent,
    ApartmentPaymentsComponent,
    ApartmentReadingsComponent,
    RouterLink,
    NgbTooltip,
    NgxSkeletonLoaderComponent,
    ApartmentContractsComponent,
    ApartmentCardComponent,
    ContractsComponent,
    BalanceComponent,
    ResourceByIdPipe,
    DocumentsComponent,
    MetersComponent
  ],
  templateUrl: './apartment-profile.component.html',
  styleUrl: './apartment-profile.component.scss'
})
export class ApartmentProfileComponent extends BaseLoadComponent<Apartment> implements OnInit {
  @Input() id: number;

  public breadCrumbItems = [];

  public apartmentInformation: ApartmentInformation;
  public renterInformation: RenterInformation;

  public periodFormControl = new FormControl(new Date());

  public activeTab = TabId.Contracts;
  public location = inject(Location);

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected service = inject(ApartmentsService);

  fullNamePipe=new FullNamePipe()
  protected readonly Tabs = TabId;
  protected readonly tabs: Tab[] = [
    { id: TabId.Contracts, titleKey: 'PROPERTY.TAB.CONTRACTS', iconClass: 'ri-grid-fill' },
    { id: TabId.Meters, titleKey: 'MENU.METERS', iconClass: 'ri-table-alt-fill' },
    { id: TabId.Readings, titleKey: 'MENU.READINGS', iconClass: 'ri-file-2-fill' },
    { id: TabId.Payments, titleKey: 'MENU.PAYMENTS', iconClass: 'ri-file-2-fill' },
    { id: TabId.Invoices, titleKey: 'APARTMENT.INVOICES', iconClass: 'ri-file-2-fill' },
    { id: TabId.Documents, titleKey: 'MAIN.DOCUMENTS', iconClass: 'las la-file' },
  ];

  ngOnInit(): void {
    const frag = this.route.snapshot.fragment as keyof typeof TabId;
    this.activeTab = TabId[frag] ?? TabId.Contracts;

    this.route.fragment
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(frag => {
        if (Object.values(TabId).includes(frag as TabId)) {
          this.activeTab = frag as TabId;
        }
      });

    this.breadCrumbItems = [
      { label: 'MENU.SETTINGS' },
      { label: 'APARTMENT.EDIT_APARTMENT', link: Resources.UniversalBillingApartments },
      { label: this.id, active: true }
    ];

    this.periodFormControl.valueChanges.subscribe(
      () => this.loadData()
    )

    super.ngOnInit();
  }

  getData(): Observable<any> {
    return this.service.getOne(this.id)
  }

  afterLoadData(data: any) {
    this.apartmentInformation = {
      apartmentId: data.id,
      building: data.building.id,
      block: data.block,
      floor: data.floor,
      number: data.number,
      assignedMeters: data.meters.length,
      numberOfResidents: data.numberOfResidents,
      size: data.size,
      status: data.status,
      isVacant: data.isVacant,
    };

    if (data.isVacant==='OCCUPIED' && data.rentalContract) {
      this.renterInformation = {
        fullName: this.fullNamePipe.transform(data.rentalContract.renter),
        idCard: data.rentalContract.renter.idCardNumber,
        taxCode: data.rentalContract.renter.taxCode,
        email: data.rentalContract.renter.email,
        phone: data.rentalContract.renter.phoneNumber,
        socialStatus: data.rentalContract.renter.socialStatus,
        birthDate: new Date(data.rentalContract.renter.birthDate),
        accountNo: data.rentalContract.renter.bankIban,
        rentType: RentType[data.rentalContract.rentType]
      }
    } else {
      this.renterInformation = null;
    }
  }


  selectTab(tab: Tab) {
    this.router.navigate([], {
      fragment: tab.id,
      replaceUrl: true,
      queryParamsHandling: 'preserve'
    });
  }

}
